import { DEFAULT_OPENED_ADVICES } from 'constants/ChecklistConstants';
import * as appointmentTypes from 'modules/appointments/actionTypes';
import * as campaignFilesTypes from 'modules/campaignFiles/actionTypes';
import * as types from 'modules/checklists/actionTypes';
import type { CloseAllItemsAction, CloseItemsAction, OpenItemsAction } from 'modules/checklists/types';
import * as interventionTypes from 'modules/interventions/actionTypes';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

import { getItems } from '../../../../../../services/ChecklistUtils';

export type Slice = string[];

type AdviceLike = { severity?: string; comment?: string; medias?: unknown };
type InterventionLike = { checklists?: Array<{ items?: Array<{ categoryId: string; advices?: AdviceLike[] }> }> };

const initialState: Slice = [];

const isItemOpen = ({ severity, comment, medias }: AdviceLike): boolean =>
  (Boolean(severity) && DEFAULT_OPENED_ADVICES.includes(severity)) || Boolean(comment) || Boolean(medias);

const updateFromIntervention = (state: Slice, { payload: intervention }: { payload: InterventionLike }): Slice => {
  const items = getItems(intervention.checklists as never);

  return uniq(
    items.reduce<Slice>((acc, item) => {
      const isOpened = item.advices?.some(advice => isItemOpen(advice));
      if (isOpened) {
        return [...acc, item.categoryId as string];
      }
      return acc.filter(categoryId => categoryId !== item.categoryId);
    }, state)
  );
};

const open = (state: Slice, { payload: categoryId }: { payload: string }): Slice => uniq([...state, categoryId]);

const close = (state: Slice, { payload: categoryId }: { payload: string }): Slice =>
  state.filter(itemId => itemId !== categoryId);

const openMultiple = (state: Slice, { payload: ids }: OpenItemsAction): Slice => uniq([...state, ...ids]);

const closeMultiple = (state: Slice, { payload: ids }: CloseItemsAction): Slice =>
  state.filter(itemId => !ids.includes(itemId));

const closeAll = (_: Slice, __: CloseAllItemsAction): Slice => [];

const updateFromAppointment = (state: Slice, { payload: appointment }: { payload: { intervention: InterventionLike } }): Slice =>
  updateFromIntervention(state, { payload: appointment.intervention });

const updateFromImportSuccess = (
  state: Slice,
  action: { type: string; payload: { intervention: InterventionLike } }
): Slice => {
  const { intervention } = action.payload;
  return updateFromIntervention(state, { ...action, payload: intervention });
};

export default createReducer(initialState, {
  // From intervention
  [interventionTypes.FETCH_INTERVENTION_SUCCESS]: updateFromIntervention,
  [interventionTypes.UPDATE_INTERVENTION_SUCCESS]: updateFromIntervention,
  [interventionTypes.IMPORT_CHECKLISTS_SUCCESS]: updateFromIntervention,
  [interventionTypes.IMPORT_TIRE_REPORT_SUCCESS]: updateFromIntervention,
  [campaignFilesTypes.FETCH_CAMPAIGN_FILE_SUCCESS]: updateFromIntervention,
  [interventionTypes.IMPORT_AUDITS_OR_TSCANS_SUCCESS]: updateFromImportSuccess,
  [interventionTypes.IMPORT_CHECKLISTS_GROUPS_SUCCESS]: updateFromIntervention,
  [interventionTypes.IMPORT_WORKS_INTO_INTERVENTION_SUCCESS]: updateFromIntervention,
  // From appointment
  [appointmentTypes.FETCH_APPOINTMENT_SUCCESS]: updateFromAppointment,
  // actions
  [types.OPEN_ITEM]: open,
  [types.CLOSE_ITEM]: close,
  [types.OPEN_ITEMS]: openMultiple,
  [types.CLOSE_ITEMS]: closeMultiple,
  [types.CLOSE_ALL_ITEMS]: closeAll,
});
